import { useState, useEffect } from 'react';
import {
  FiGrid,
  FiSmartphone,
  FiTrendingUp,
  FiDollarSign,
  FiBell,
  FiAlertTriangle,
  FiUser,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const DashboardSidebar = ({ activeTab, handleTabClick, sidebarOpen, setSidebarOpen, unreadAlerts, isMobile }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Erro ao sair:", error);
    }
  };

  const [isMinimized, setIsMinimized] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarMinimized');
      return saved === 'true';
    }
    return false;
  });

  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('sidebarMinimized', isMinimized);
  }, [isMinimized]);

  // Remover as seções complexas e agrupar para o estilo simplificado do print
  const menuItems = [
    { id: 'overview', name: 'Dashboard', Icon: FiGrid },
    { id: 'apps', name: 'Meus Apps', Icon: FiSmartphone },
    { id: 'notifications', name: 'Notificações', Icon: FiBell },
    { id: 'analytics', name: 'Analytics', Icon: FiTrendingUp },
    { id: 'financial', name: 'Financeiro', Icon: FiDollarSign },
    { id: 'alerts', name: 'Alertas', Icon: FiAlertTriangle, badge: unreadAlerts },
    { id: 'profile', name: 'Meu Perfil', Icon: FiUser },
    { id: 'settings', name: 'Configurações', Icon: FiSettings }
  ];

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // No mobile fechado, não mostrar o sidebar
  if (isMobile && !sidebarOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay apenas no MOBILE quando sidebar está aberta */}
      {isMobile && (
        <div
          className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-all duration-300 ease-out ${sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
            }`}
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar - Desktop: fixo (relative), Mobile: overlay (fixed) */}
      <div
        className={`
          ${isMobile ? 'fixed' : 'sticky'}
          ${isMobile ? 'top-0 h-full' : 'top-0 h-screen'}
          left-0
          ${isMobile ? 'z-50' : 'z-10'}
          ${isMinimized ? 'w-16' : 'w-64'}
          ${isMobile ? 'max-w-full' : ''}
          bg-white border-r border-gray-100 shadow-sm
          transform ${isMobile ? 'transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]' : 'transition-all duration-300 ease-out'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col flex-shrink-0 overflow-hidden
        `}
      >
        {/* Conteúdo do Sidebar */}
        <div className="flex flex-col h-full flex-1">
          {/* LOGO E TOGGLE AREA */}
          <div className={`h-16 flex items-center ${isMinimized ? 'justify-center' : 'justify-end px-4'} border-b border-gray-50/50`}>
            {!isMobile && (
              <button
                onClick={toggleMinimize}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                title={isMinimized ? "Expandir menu" : "Minimizar menu"}
              >
                {isMinimized ? <FiChevronRight className="w-5 h-5" /> : <FiChevronLeft className="w-5 h-5" />}
              </button>
            )}
          </div>

          <nav className={`flex-1 ${isMinimized ? 'px-2 py-4 overflow-hidden' : 'p-4 overflow-y-auto custom-scrollbar'}`}>
            <ul className="space-y-2">
              {menuItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleTabClick(item.id)}
                    className={`
                      flex items-center
                      group relative transition-all duration-200 ease-out
                      ${isMinimized ? 'w-11 h-11 mx-auto justify-center rounded-md' : 'w-full h-11 px-3 justify-start rounded-md'}
                      ${activeTab === item.id
                        ? 'bg-gray-800 text-white font-medium shadow-sm shadow-gray-800/20'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    title={isMinimized ? item.name : ''}
                  >
                    <div className={`
                      flex items-center w-full
                      ${isMinimized ? 'justify-center' : 'justify-start gap-3'}
                    `}>
                      <span className={`
                        flex items-center justify-center flex-shrink-0
                        transition-all duration-200 ease-out
                        ${activeTab === item.id ? 'scale-110 text-white' : 'scale-100'}
                      `}>
                        <item.Icon className="w-5 h-5" />
                      </span>

                      {!isMinimized && (
                        <span className={`
                          text-left text-sm truncate flex-1 flex items-center mt-0.5
                          ${activeTab === item.id ? 'font-medium text-white tracking-wide' : 'font-light'}
                        `}>
                          {item.name}
                        </span>
                      )}
                    </div>

                    {item.badge > 0 && (
                      <span className={`
                        bg-red-500 text-white text-[10px] font-bold rounded-full 
                        flex items-center justify-center flex-shrink-0
                        animate-pulse shadow-sm
                        ${isMinimized
                          ? 'absolute -top-1 -right-1 w-4 h-4'
                          : 'min-w-[20px] h-5 px-1.5 ml-2'
                        }
                      `}>
                        {isMinimized ? '' : item.badge}
                      </span>
                    )}

                    {/* Tooltip for minimized */}
                    {isMinimized && (
                      <div className={`
                        absolute left-full ml-3 px-3 py-2 rounded-lg text-sm
                        opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out
                        whitespace-nowrap z-50 pointer-events-none
                        transform translate-x-2 group-hover:translate-x-0
                        bg-white text-gray-900 shadow-xl border border-gray-100
                      `}>
                        <div className="font-medium">{item.name}</div>
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* PROMO BANNER */}
          {isMobile && !isMinimized && (
            <div className="mx-4 mb-4 p-4 rounded-2xl bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a] text-white shadow-md border border-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
              <h4 className="text-sm font-bold mb-1.5 tracking-tight">
                CriaApp Pro
              </h4>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed font-normal">
                Entre na lista de espera e garanta 40% de desconto no lançamento.
              </p>
              <button
                className="w-full py-2 bg-white text-black text-xs font-semibold rounded-xl text-center hover:bg-gray-100 transition-colors"
              >
                Entrar na lista
              </button>
            </div>
          )}

          {/* USER ACCOUNT AREA / LOGOUT */}
          {isMobile && (
            <div className={`border-t border-gray-50/50 ${isMinimized ? 'px-2 py-4' : 'p-4'}`}>
              <button
                onClick={handleLogout}
                className={`
                  flex items-center
                  group relative transition-all duration-200 ease-out
                  ${isMinimized ? 'w-11 h-11 mx-auto justify-center rounded-md' : 'w-full h-11 px-3 justify-start gap-3 rounded-md'}
                  text-gray-500 hover:bg-red-50 hover:text-red-600
                `}
                title={isMinimized ? "Sair da conta" : ""}
              >
                <span className={`
                  flex items-center justify-center flex-shrink-0
                  transition-all duration-200 ease-out group-hover:scale-110
                `}>
                  <FiLogOut className="w-5 h-5" />
                </span>

                {!isMinimized && (
                  <span className="text-left text-sm font-medium truncate flex-1 flex items-center mt-0.5">
                    Sair
                  </span>
                )}

                {/* Tooltip for minimized */}
                {isMinimized && (
                  <div className={`
                    absolute left-full ml-3 px-3 py-2 rounded-lg text-sm
                    opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out
                    whitespace-nowrap z-50 pointer-events-none
                    transform translate-x-2 group-hover:translate-x-0
                    bg-white text-gray-900 shadow-xl border border-gray-100 font-medium
                  `}>
                    Sair da conta
                  </div>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Botões de Controle - No mobile só mostrar quando sidebar estiver aberto */}
      {(sidebarOpen || !isMobile) && (
        <div
          className={`
            fixed bottom-4 z-40
            will-change-transform
            ${sidebarOpen ? 'left-2' : '-left-full md:left-2'}
            transition-all duration-250 ease-out
          `}
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="space-y-2">
            {/* Botão de fechar (mobile) */}
            {isMobile && sidebarOpen && (
              <div className="flex justify-center">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    p-3 rounded-xl transition-all duration-250 ease-out 
                    flex items-center gap-2 shadow-sm h-12 select-none
                    bg-white text-gray-600 hover:bg-gray-50 border border-gray-200
                  `}
                >
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {!isMinimized && (
                    <span className="font-medium truncate">Fechar Menu</span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
// Para subir para o github sem interferencia da vercel
export default DashboardSidebar;